import React from 'react';
import { DollarSign, TrendingUp, PieChart, Shield, Sparkles } from 'lucide-react';
import { TaraCard, TaraCardHeader, TaraCardTitle, TaraCardContent } from '../ui/TaraCard';
import TaraBadge from '../ui/TaraBadge';

const WealthBarometer = () => {
  // Wealth dimensions
  const wealthMetrics = {
    stability: 72,
    growth: 65,
    risk: 45, // Lower is better
    luck: 78,
    savings: 68,
    investment: 55,
  };

  // Money compass - suitability
  const moneyCompass = [
    { type: 'Entrepreneurship', score: 75, icon: Sparkles, color: '#f59e0b' },
    { type: 'Trading/Stocks', score: 55, icon: TrendingUp, color: '#ef4444' },
    { type: 'Real Estate', score: 82, icon: Shield, color: '#10b981' },
    { type: 'Long-term Investing', score: 88, icon: PieChart, color: '#6366f1' },
  ];

  // Financial phases
  const financialPhases = [
    { year: 2025, phase: 'Accumulation', amount: '+15%', color: '#10b981' },
    { year: 2027, phase: 'Consolidation', amount: '+8%', color: '#f59e0b' },
    { year: 2030, phase: 'Growth Boom', amount: '+35%', color: '#22c55e' },
  ];

  // Daily financial energy (Moon-based)
  const dailyEnergy = 73;

  return (
    <div className="space-y-6">
      {/* Overall Wealth Score */}
      <TaraCard variant="cosmic" className="text-center">
        <TaraCardContent className="p-8">
          <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-green-100 to-green-200 mb-4">
            <div className="text-center">
              <DollarSign className="w-12 h-12 text-green-600 mx-auto mb-1" />
              <div className="font-serif text-2xl font-medium text-green-700">
                {Math.round(Object.values(wealthMetrics).reduce((a, b) => a + b) / Object.keys(wealthMetrics).length)}
              </div>
              <div className="text-xs text-green-600">Wealth Index</div>
            </div>
          </div>
          <h3 className="font-serif text-xl font-medium text-stone-900 mb-2">
            Financial Health: Good
          </h3>
          <p className="text-sm text-stone-600">
            Based on 2nd house + Jupiter + Venus
          </p>
        </TaraCardContent>
      </TaraCard>

      {/* Wealth Barometer Dimensions */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle>Wealth Barometer</TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            6 dimensions of financial wellness
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-4">
            {Object.entries(wealthMetrics).map(([key, value]) => {
              const isInverted = key === 'risk';
              const displayValue = isInverted ? 100 - value : value;
              const color = displayValue >= 70 ? '#10b981' : displayValue >= 50 ? '#f59e0b' : '#ef4444';
              
              return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-stone-700 capitalize">
                      {key}
                    </span>
                    <span className="text-sm font-medium" style={{ color }}>
                      {displayValue}%
                    </span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-500"
                      style={{ width: `${displayValue}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Money Compass */}
      <TaraCard variant="sacred">
        <TaraCardHeader>
          <TaraCardTitle>Money Compass</TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Best wealth-building strategies for you
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {moneyCompass.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.type}
                  className="p-4 rounded-lg border-2"
                  style={{ borderColor: `${item.color}40`, backgroundColor: `${item.color}10` }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: item.color }}
                    >
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm text-stone-900">{item.type}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-stone-600">Suitability</span>
                    <span className="text-sm font-medium" style={{ color: item.color }}>
                      {item.score}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Daily Financial Energy */}
      <TaraCard>
        <TaraCardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-serif font-medium text-stone-900 mb-1">
                Today's Financial Energy
              </h4>
              <p className="text-xs text-stone-600">
                Based on Moon transit
              </p>
            </div>
            <div className="text-right">
              <div className="font-serif text-3xl font-medium text-green-600">
                {dailyEnergy}%
              </div>
              <TaraBadge variant="calm" size="sm">Good</TaraBadge>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Financial Phases Timeline */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle>Wealth Growth Timeline</TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Projected financial phases
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-3">
            {financialPhases.map((phase) => (
              <div
                key={phase.year}
                className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-white rounded-lg border border-green-100"
              >
                <div>
                  <div className="font-serif font-medium text-stone-900">{phase.year}</div>
                  <div className="text-sm text-stone-600">{phase.phase}</div>
                </div>
                <div className="text-right">
                  <div className="font-medium" style={{ color: phase.color }}>
                    {phase.amount}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TaraCardContent>
      </TaraCard>
    </div>
  );
};

export default WealthBarometer;